import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuth0 } from '@auth0/auth0-react'
import { Configuration } from './generated/configuration'
import { env } from '@/lib/env'
import { CalendarsApi } from './generated/api/calendars-api'
import { EventsApi } from './generated/api/events-api'
import { PhoneNumbersApi } from './generated/api/phone-numbers-api'
import { UsersApi } from './generated/api/users-api'
import { OrganizationsApi } from './generated/api/organizations-api'
import { LocationsApi } from './generated/api/locations-api'
import { EventTypesApi } from './generated/api/event-types-api'
import { CallLogsApi } from './generated/api/call-logs-api'
import { AgentsApi } from './generated/api/agents-api'
import { VoicesApi } from './generated/api/voices-api'
import { AnalyticsApi } from './generated/api/analytics-api'
import { CallbacksApi } from './generated/api/callbacks-api'
import { TransferDestinationsApi } from './generated/api/transfer-destinations-api'
import { ActivitiesApi } from './generated/api/activities-api'
import { TranscriptsApi } from './generated/api/transcripts-api'
import { ApiKeysApi } from './generated/api/api-keys-api'
import { OutboundCallsApi } from './generated/api/outbound-calls-api'
import { IntegrationsApi } from './generated/api/integrations-api'
import { IntegrationConflictsApi } from './generated/api/integration-conflicts-api'
import { IntegrationSyncApi } from './generated/api/integration-sync-api'
import { ContactsApi } from './generated/api/contacts-api'
import { KnowledgeBaseApi } from './generated/api/knowledge-base-api'
import { ToolsApi } from './generated/api/tools-api'
import { CustomToolsApi } from './generated/api/custom-tools-api'
import { PromptsApi } from './generated/api/prompts-api'
import { TestCallsApi } from './generated/api/test-calls-api'
import { TestChatsApi } from './generated/api/test-chats-api'
import { NotificationReceiversApi } from './generated/api/notification-receivers-api'
import { AssistantApi } from './generated/api/assistant-api'
import { AnalyticsUsageApi } from './generated/api/analytics-usage-api'
import { BillingApi } from './generated/api/billing-api'
import { AdminBillingApi } from './generated/api/admin-billing-api'
import { AdminOrganizationsApi } from './generated/api/admin-organizations-api'
import { AdminUsersApi } from './generated/api/admin-users-api'
import { AdminPhoneNumbersApi } from './generated/api/admin-phone-numbers-api'
import { AdminTelephonyApi } from './generated/api/admin-telephony-api'
import { PromptTemplatesApi } from './generated/api/prompt-templates-api'
import { GoogleCalendarApi } from './generated/api/google-calendar-api'
import { WidgetKeysApi } from './generated/api/widget-keys-api'
import { BookingKeysApi } from './generated/api/booking-keys-api'

const API_BASE_URL = env.API_BASE_URL

let isRedirectingToLogin = false

export const createApiClient = (
  getAccessToken?: (opts?: { forceRefresh?: boolean }) => Promise<string>,
  onUnauthorized?: () => void
): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'de', // Request German-localized error messages from the backend
    },
  })

  const redirectToLogin = () => {
    if (!isRedirectingToLogin) {
      isRedirectingToLogin = true
      onUnauthorized?.()
    }
  }

  client.interceptors.request.use(
    async (config) => {
      if (getAccessToken) {
        try {
          const token = await getAccessToken()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        } catch (error) {
          // Token acquisition failed (e.g. refresh token expired/revoked):
          // don't send an unauthenticated request — bounce to login instead.
          console.error('Failed to get access token:', error)
          redirectToLogin()
          throw error
        }
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined

      // 401: the access token may be stale — force a refresh via the refresh
      // token and retry once. If that fails too, the session is gone → login.
      if (status === 401 && getAccessToken && originalRequest) {
        if (!originalRequest._retry) {
          originalRequest._retry = true
          try {
            const token = await getAccessToken({ forceRefresh: true })
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return client(originalRequest)
            }
          } catch {
            // fall through to redirect
          }
        }
        redirectToLogin()
        return Promise.reject(error)
      }

      // 5xx: strip response body to prevent leaking server internals.
      // User-facing message is handled by getErrorMessage() in lib/error.ts.
      if (status && status >= 500) {
        error.response.data = undefined
        return Promise.reject(error)
      }

      // 4xx: pass through unmodified so error.response.data.detail is available
      return Promise.reject(error)
    }
  )

  return client
}

export const useApiClient = (): AxiosInstance => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()

  return createApiClient(
    (opts) =>
      getAccessTokenSilently(opts?.forceRefresh ? { cacheMode: 'off' } : undefined),
    () => {
      void loginWithRedirect()
    }
  )
}

// Generated API clients with Auth0 integration
export const useGeneratedApiClients = () => {
  const axiosInstance = useApiClient()
  const config = new Configuration({
    basePath: API_BASE_URL,
  })

  return {
    calendarsApi: new CalendarsApi(config, API_BASE_URL, axiosInstance),
    eventsApi: new EventsApi(config, API_BASE_URL, axiosInstance),
    phoneNumbersApi: new PhoneNumbersApi(config, API_BASE_URL, axiosInstance),
    usersApi: new UsersApi(config, API_BASE_URL, axiosInstance),
    organizationsApi: new OrganizationsApi(config, API_BASE_URL, axiosInstance),
    locationsApi: new LocationsApi(config, API_BASE_URL, axiosInstance),
    eventTypesApi: new EventTypesApi(config, API_BASE_URL, axiosInstance),
    callLogsApi: new CallLogsApi(config, API_BASE_URL, axiosInstance),
    agentsApi: new AgentsApi(config, API_BASE_URL, axiosInstance),
    voicesApi: new VoicesApi(config, API_BASE_URL, axiosInstance),
    analyticsApi: new AnalyticsApi(config, API_BASE_URL, axiosInstance),
    callbacksApi: new CallbacksApi(config, API_BASE_URL, axiosInstance),
    transferDestinationsApi: new TransferDestinationsApi(config, API_BASE_URL, axiosInstance),
    activitiesApi: new ActivitiesApi(config, API_BASE_URL, axiosInstance),
    transcriptsApi: new TranscriptsApi(config, API_BASE_URL, axiosInstance),
    apiKeysApi: new ApiKeysApi(config, API_BASE_URL, axiosInstance),
    outboundCallsApi: new OutboundCallsApi(config, API_BASE_URL, axiosInstance),
    integrationsApi: new IntegrationsApi(config, API_BASE_URL, axiosInstance),
    integrationConflictsApi: new IntegrationConflictsApi(config, API_BASE_URL, axiosInstance),
    integrationSyncApi: new IntegrationSyncApi(config, API_BASE_URL, axiosInstance),
    contactsApi: new ContactsApi(config, API_BASE_URL, axiosInstance),
    knowledgeBaseApi: new KnowledgeBaseApi(config, API_BASE_URL, axiosInstance),
    toolsApi: new ToolsApi(config, API_BASE_URL, axiosInstance),
    customToolsApi: new CustomToolsApi(config, API_BASE_URL, axiosInstance),
    promptsApi: new PromptsApi(config, API_BASE_URL, axiosInstance),
    testCallsApi: new TestCallsApi(config, API_BASE_URL, axiosInstance),
    testChatsApi: new TestChatsApi(config, API_BASE_URL, axiosInstance),
    notificationReceiversApi: new NotificationReceiversApi(config, API_BASE_URL, axiosInstance),
    assistantApi: new AssistantApi(config, API_BASE_URL, axiosInstance),
    analyticsUsageApi: new AnalyticsUsageApi(config, API_BASE_URL, axiosInstance),
    billingApi: new BillingApi(config, API_BASE_URL, axiosInstance),
    adminBillingApi: new AdminBillingApi(config, API_BASE_URL, axiosInstance),
    adminOrganizationsApi: new AdminOrganizationsApi(config, API_BASE_URL, axiosInstance),
    adminUsersApi: new AdminUsersApi(config, API_BASE_URL, axiosInstance),
    adminPhoneNumbersApi: new AdminPhoneNumbersApi(config, API_BASE_URL, axiosInstance),
    adminTelephonyApi: new AdminTelephonyApi(config, API_BASE_URL, axiosInstance),
    promptTemplatesApi: new PromptTemplatesApi(config, API_BASE_URL, axiosInstance),
    googleCalendarApi: new GoogleCalendarApi(config, API_BASE_URL, axiosInstance),
    widgetKeysApi: new WidgetKeysApi(config, API_BASE_URL, axiosInstance),
    bookingKeysApi: new BookingKeysApi(config, API_BASE_URL, axiosInstance),
  }
}
